import Box from "@/components/Box";
import RichText from "@/components/RichText";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { COMING_SOON, OUT_OF_STOCK, UNPUBLISHED } from "@/core/constants";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import { useTheme } from "styled-components";

import AddOnDetail from "../../shared/ui/AddOnDetail";

export function AddonsHeader({
  title = "Additional marker add ons",
  description = "This test has additional markers that can be included in your purchase. They will become available once you have added the base test to your basket."
}) {
  return (
    <Box>
      <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="130%">
        {title}
      </Box>
      {description ? (
        <>
          <Spacer py={[7.5, 7.5, 15]} />
          <RichText
            fontFamily="gilroyMedium"
            fontSize={[14, 14, 16]}
            data-testid="additionalMarkerAddOnsDescription"
          >
            {description}
          </RichText>
        </>
      ) : null}
    </Box>
  );
}

export function AddonsStockMessage() {
  const { basket } = useOrganisationBasketContext();

  if (basket?.purchaseAsStock || basket?.multiplePatientsEnabled) {
    return (
      <Box color="red" fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
        Add-ons unavailable when purchasing products{" "}
        {basket?.purchaseAsStock ? "as stock" : "for multiple patients"}
      </Box>
    );
  }

  return (
    <Box
      color="dark"
      fontFamily="gilroyRegular"
      fontSize={[14, 14, 16]}
      fontStyle="italic"
      data-testid="addons-unavailable"
    >
      Add-ons will be unavailable when purchasing products as stock. They can be added to a
      stock purchase at the point of test registration.
    </Box>
  );
}

export function useAddonState(addon) {
  const { testProduct } = useTestProductModalContext();
  const { basket, hasBasketGotProduct, hasProductGotAddon } = useOrganisationBasketContext();
  const basketHasGotProduct = hasBasketGotProduct(testProduct.id);

  if (basketHasGotProduct && !basket?.purchaseAsStock && !basket?.multiplePatientsEnabled) {
    if (addon.status === OUT_OF_STOCK || addon.status === COMING_SOON) {
      return "unavailable";
    }
    if (hasProductGotAddon(testProduct.id, addon.id)) {
      return "selected";
    }
    return "unselected";
  }
  return "unavailable";
}

export function AddonsList({ addons, onRemovingAddon, onAddingOn }) {
  return (
    <Stack
      gap={1}
      maxHeight="calc(130.8px * 5 + 5 * 1px)"
      style={{
        overflow: "auto"
      }}
    >
      {addons.filter(addon => addon.status !== UNPUBLISHED).map(addon => (
        <AddonItem
          key={addon.id}
          addon={addon}
          onRemove={() => onRemovingAddon({ addon })}
          onAdd={() => onAddingOn({ addon })}
        />
      ))}
    </Stack>
  );
}

function AddonItem({ addon, onRemove, onAdd }) {
  const state = useAddonState(addon);

  return (
    <AddOnDetail
      name={addon.name}
      description={addon.content.description}
      price={addon.tradeCurrentPrice}
      rrpPrice={addon.exvatFullPrice}
      state={state}
      text={
        addon.status === OUT_OF_STOCK || addon.status === COMING_SOON
          ? addon.status
          : undefined
      }
      onRemove={onRemove}
      onAdd={onAdd}
    />
  );
}

export default function Addons({ addons, onRemovingAddon, onAddingOn }) {
  const theme = useTheme();

  return (
    <PanelBoxV2
      maxWidth={768}
      outer={{
        bg: "haze",
        pt: theme.spacing.section.pt,
        pb: theme.spacing.section.pb,
        px: [20, 20, 40],
        "data-component-name": "Addons"
      }}
      stacked
      gap={40}
    >
      <AddonsHeader />
      <AddonsStockMessage />
      <AddonsList addons={addons} onRemovingAddon={onRemovingAddon} onAddingOn={onAddingOn} />
    </PanelBoxV2>
  );
}
